import { Fragment, useMemo } from "react";
import { format, isToday, isYesterday } from "date-fns";

import { InboxItemCard } from "@/components/inbox/inbox-item-card";
import type { InboxItem, SourceConfig } from "@/lib/api";

interface Props {
  items: InboxItem[];
  sources: SourceConfig[];
  expandedId?: string;
  selectedInboxItemIds?: Set<string>;
  freshInboxItemIds?: Set<string>;
  onToggle: (item: InboxItem) => void;
  onSelectedChange?: (item: InboxItem, selected: boolean) => void;
  onAnalyzeItem?: (item: InboxItem) => void;
  timeBasis?: "captured" | "source";
  isLoading?: boolean;
  isError?: boolean;
  errorMessage?: string;
}

interface DateGroup {
  key: string;
  label: string;
  items: InboxItem[];
}

/** Group inbox items (already DESC) by calendar day. Each item keeps the order
 *  the server sent, so within a group it's still most-recent-first. */
function groupByDay(items: InboxItem[], timeBasis: "captured" | "source"): DateGroup[] {
  const groups = new Map<string, DateGroup>();
  for (const item of items) {
    const ts = itemTimestamp(item, timeBasis);
    if (!ts) continue;
    const d = new Date(ts);
    const key = format(d, "yyyy-MM-dd");
    let group = groups.get(key);
    if (!group) {
      group = { key, label: dayLabel(d), items: [] };
      groups.set(key, group);
    }
    group.items.push(item);
  }
  return Array.from(groups.values());
}

function dayLabel(d: Date): string {
  if (isToday(d)) return "Today";
  if (isYesterday(d)) return "Yesterday";
  return format(d, "EEEE, MMM d");
}

/** Vertical feed of inbox items, grouped by day with sticky headers. */
export function InboxList({
  items,
  sources,
  expandedId,
  selectedInboxItemIds,
  freshInboxItemIds,
  onToggle,
  onSelectedChange,
  onAnalyzeItem,
  timeBasis = "captured",
  isLoading,
  isError,
  errorMessage,
}: Props) {
  const groups = useMemo(() => groupByDay(items, timeBasis), [items, timeBasis]);
  const sourceById = useMemo(
    () => new Map(sources.map((s) => [s.id, s])),
    [sources],
  );

  if (isError) {
    return (
      <div className="mx-auto mt-16 flex max-w-xl flex-col items-center gap-2 px-6 text-center">
        <div className="text-sm font-semibold text-red-300">
          Signals API unavailable
        </div>
        <div className="text-xs leading-5 text-muted-fg">
          {errorMessage ?? "Failed to load inbox items."}
        </div>
      </div>
    );
  }

  if (isLoading && items.length === 0) {
    return (
      <div className="flex h-40 items-center justify-center text-sm text-muted-fg">
        Loading inbox…
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex h-40 items-center justify-center text-sm text-muted-fg">
        No inbox items match the current filters.
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {groups.map((g) => (
        <Fragment key={g.key}>
          <div className="sticky top-0 z-10 border-b border-border/60 bg-bg/95 px-5 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-fg backdrop-blur">
            {g.label}
            <span className="ml-2 font-normal text-muted-fg/70">
              {g.items.length}
            </span>
          </div>
          {g.items.map((item) => (
            <InboxItemCard
              key={item.id}
              item={item}
              source={sourceById.get(item.source_id)}
              expanded={item.id === expandedId}
              selected={selectedInboxItemIds?.has(item.id) ?? false}
              isFresh={freshInboxItemIds?.has(item.id) ?? false}
              onToggle={onToggle}
              onSelectedChange={onSelectedChange}
              onAnalyzeItem={onAnalyzeItem}
              timeBasis={timeBasis}
            />
          ))}
        </Fragment>
      ))}
    </div>
  );
}

function itemTimestamp(item: InboxItem, timeBasis: "captured" | "source"): string | null {
  return timeBasis === "source"
    ? item.item_timestamp ?? item.captured_at
    : item.captured_at;
}
